import { useLocation } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { DateTime } from 'luxon';

import { useAppContext } from '@app/contexts';
import { UserSearchDto, useSaveRecentSearchesMutation } from '@app/hooks';
import { addEntryToDashboardList } from '@app/utils';
import { searchInterface } from '@app/constants';

import {
  SEARCH_HISTORY_KEY,
  SEARCH_OPTION,
  SearchInstanceDto,
  SearchState,
  VIEWED_RECORDS_KEY,
} from '../constants';

export interface SearchHistoryEntry extends SearchState {
  searchInterface: string;
  totalRecords: number;
  timestamp: string;
  searchType: SEARCH_OPTION;
  requestParams?: string;
  id?: string;
}

export interface RecordsWithTimestamp {
  instanceId: string;
  timestamp: string;
  id?: string;
}

export type TimestampedRecordWithData = RecordsWithTimestamp &
  SearchInstanceDto;

export const useRecentActivityHistory = () => {
  const { isLoggedIn } = useAppContext();
  const [searchHistory, setSearchHistory] = useSessionStorage<UserSearchDto[]>(
    SEARCH_HISTORY_KEY,
    []
  );
  const [viewedRecords, setViewedRecords] = useSessionStorage<
    RecordsWithTimestamp[]
  >(VIEWED_RECORDS_KEY, []);
  const saveRecentSearchMutation = useSaveRecentSearchesMutation();
  const { search: urlQuery } = useLocation();

  const addSearchHistoryEntry = (entry: SearchState, totalRecords: number) => {
    const searchEntry = {
      query: entry.query as string,
      requestParams: urlQuery,
      resultCount: totalRecords,
      searchType: entry.option,
      interface: searchInterface,
      date: DateTime.now().toISO(),
    };

    if (isLoggedIn) {
      saveRecentSearchMutation.mutate({
        searches: [searchEntry],
      });
    } else {
      setSearchHistory(addEntryToDashboardList(searchEntry, searchHistory));
    }
  };

  const addViewedRecord = (record: RecordsWithTimestamp) => {
    setViewedRecords(addEntryToDashboardList(record, viewedRecords));
  };

  return {
    addSearchHistoryEntry,
    addViewedRecord,
  };
};
